import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import AceEditor from 'react-ace'
import 'ace-builds/src-noconflict/mode-json'
import 'ace-builds/src-noconflict/theme-xcode'
import { Dialog, DialogContent, IconButton, makeStyles, Snackbar, Tooltip } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import CloseIcon from '@material-ui/icons/Close'
import FullscreenIcon from '@material-ui/icons/Fullscreen'
import FileCopyIcon from '@material-ui/icons/FileCopy'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { flattenNoFollow } from '../../../../../../../utils/flatten'
import { useEditor } from './EditorContext'

const useStyles = makeStyles(theme => ({
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0
  },
  toolbar: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.5, 1),
    borderBottom: '1px solid var(--color-neutral-200)',
    backgroundColor: 'var(--color-neutral-050)'
  },
  modeToggle: {
    display: 'flex',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    overflow: 'hidden'
  },
  modeBtn: {
    padding: '4px 10px',
    fontSize: 'var(--text-size-xs)',
    fontWeight: 600,
    color: 'var(--color-neutral-600)',
    backgroundColor: 'white',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    '&:not(:last-child)': { borderRight: '1px solid var(--color-neutral-300)' },
    '&:hover': { backgroundColor: 'var(--color-neutral-100)' },
    '&:disabled': { opacity: 0.4, cursor: 'not-allowed' }
  },
  modeBtnActive: {
    backgroundColor: 'var(--color-primary-500)',
    color: 'white',
    '&:hover': { backgroundColor: 'var(--color-primary-600)' }
  },
  spacer: { flex: 1 },
  iconBtn: {
    padding: 4,
    color: 'var(--color-neutral-400)',
    '&:hover': { color: 'var(--color-primary-600)' },
    '&:disabled': { color: 'var(--color-neutral-200)' }
  },
  body: {
    flex: 1,
    overflow: 'auto',
    minHeight: 0,
    backgroundColor: 'white'
  },
  bodyFlush: {
    flex: 1,
    minHeight: 0,
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column'
  },
  empty: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: 'var(--color-neutral-400)',
    fontSize: 'var(--text-size-sm)',
    fontStyle: 'italic'
  },
  errorBox: {
    padding: theme.spacing(1.5),
    fontFamily: 'var(--font-mono, monospace)',
    fontSize: '11px',
    color: 'var(--color-danger-700, #b91c1c)',
    backgroundColor: 'var(--color-danger-050, #fef2f2)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '11px',
    fontFamily: 'var(--font-mono, monospace)'
  },
  th: {
    padding: theme.spacing(0.5, 1),
    textAlign: 'left',
    fontWeight: 600,
    fontSize: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    color: 'var(--color-neutral-500)',
    borderBottom: '1px solid var(--color-neutral-200)',
    backgroundColor: 'var(--color-neutral-050)',
    position: 'sticky',
    top: 0
  },
  td: {
    padding: theme.spacing(0.5, 1),
    borderBottom: '1px solid var(--color-neutral-100)',
    color: 'var(--color-neutral-800)',
    verticalAlign: 'top',
    whiteSpace: 'nowrap'
  },
  cellMuted: {
    color: 'var(--color-neutral-400)',
    fontStyle: 'italic'
  },
  cellEllipsis: {
    color: 'var(--color-neutral-500)',
    cursor: 'help'
  },
  cellTooltipPre: {
    margin: 0,
    fontFamily: 'var(--font-mono, monospace)',
    fontSize: '11px',
    maxHeight: 320,
    maxWidth: 480,
    overflow: 'auto',
    whiteSpace: 'pre'
  },
  fieldsWrap: {
    padding: theme.spacing(1.5),
    fontSize: '11px',
    fontFamily: 'var(--font-mono, monospace)'
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
    borderBottom: '1px dashed var(--color-neutral-100)'
  },
  fieldAccessor: {
    color: 'var(--color-neutral-800)',
    fontWeight: 600
  },
  fieldType: {
    color: 'var(--color-neutral-500)',
    fontSize: '10px'
  },
  modalPaper: {
    width: '90vw',
    height: '85vh',
    maxWidth: 'none',
    maxHeight: 'none',
    borderRadius: 'var(--border-radius-md, 8px)',
    display: 'flex',
    flexDirection: 'column'
  },
  modalHeader: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.25, 2),
    borderBottom: '1px solid var(--color-neutral-200)'
  },
  modalTitle: {
    fontSize: 'var(--text-size-sm)',
    fontWeight: 600
  },
  modalContent: {
    flex: 1,
    overflow: 'hidden',
    padding: 0,
    display: 'flex',
    flexDirection: 'column'
  },
  treeSearchRow: {
    flexShrink: 0,
    padding: theme.spacing(0.75, 1),
    borderBottom: '1px solid var(--color-neutral-200)',
    backgroundColor: 'var(--color-neutral-050)',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75)
  },
  treeSearchInput: {
    flex: 1,
    padding: '5px 10px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-xs)',
    outline: 'none',
    backgroundColor: 'white',
    color: 'var(--color-neutral-900)',
    fontFamily: 'var(--font-mono, monospace)',
    '&:focus': { borderColor: 'var(--color-primary-400)', boxShadow: '0 0 0 2px var(--color-primary-100)' },
    '&::placeholder': { color: 'var(--color-neutral-400)', fontFamily: 'var(--font-base)' }
  },
  treeMatchCount: {
    fontSize: '11px',
    color: 'var(--color-neutral-500)',
    fontFamily: 'var(--font-mono, monospace)',
    minWidth: 60,
    textAlign: 'right'
  },
  treeBody: {
    flex: 1,
    overflow: 'auto',
    padding: theme.spacing(0.5, 0),
    fontFamily: 'var(--font-mono, monospace)',
    fontSize: '12px',
    lineHeight: 1.5,
    backgroundColor: 'white'
  },
  treeRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '2px',
    padding: '1px 8px',
    cursor: 'default',
    '&:hover': { backgroundColor: 'var(--color-neutral-050)' },
    '&:hover $treeRowActions': { opacity: 1 }
  },
  treeRowMatch: {
    backgroundColor: 'var(--color-warning-050, #fffbeb)'
  },
  treeChevron: {
    width: 16,
    height: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-neutral-500)',
    cursor: 'pointer',
    flexShrink: 0
  },
  treeChevronSpacer: {
    width: 16,
    height: 18,
    flexShrink: 0
  },
  treeKey: {
    color: 'var(--color-primary-700, #1d4ed8)',
    fontWeight: 600,
    flexShrink: 0
  },
  treeIndexKey: {
    color: 'var(--color-neutral-500)',
    fontWeight: 600,
    flexShrink: 0
  },
  treeColon: {
    color: 'var(--color-neutral-500)',
    flexShrink: 0,
    margin: '0 4px 0 0'
  },
  treeValueString: { color: 'var(--color-success-700, #15803d)', wordBreak: 'break-word' },
  treeValueNumber: { color: 'var(--color-warning-700, #b45309)' },
  treeValueBool: { color: 'var(--color-primary-700, #1d4ed8)' },
  treeValueNull: { color: 'var(--color-neutral-500)', fontStyle: 'italic' },
  treeValueSummary: { color: 'var(--color-neutral-500)', fontStyle: 'italic' },
  treeRowActions: {
    marginLeft: 'auto',
    display: 'flex',
    gap: '2px',
    opacity: 0,
    transition: 'opacity 0.15s ease'
  },
  treeActionBtn: {
    padding: 2,
    color: 'var(--color-neutral-400)',
    '&:hover': { color: 'var(--color-primary-600)', backgroundColor: 'var(--color-primary-050, #eff6ff)' }
  },
  searchHighlight: {
    backgroundColor: 'var(--color-warning-200, #fde68a)',
    borderRadius: 2,
    padding: '0 1px'
  }
}))

// Pipeline executions return `{ data: [...], meta: {...} }`. Treat `result.data`
// as the rows for table mode (mirrors legacy AutoTable behavior). Fall back to
// `result` itself if a caller hands us a raw array.
function extractRows (result) {
  if (Array.isArray(result)) return result
  if (Array.isArray(result?.data)) return result.data
  return null
}

function isTableLike (result) {
  const rows = extractRows(result)
  if (!rows || rows.length === 0) return false
  return rows.every(row => row && typeof row === 'object' && !Array.isArray(row))
}

function buildFlattenedRows (rows) {
  return rows.map(r => flattenNoFollow(r))
}

function buildColumnsFromFlattened (flatRows) {
  const cols = []
  const seen = new Set()
  for (const row of flatRows) {
    for (const k of Object.keys(row || {})) {
      if (!seen.has(k)) { seen.add(k); cols.push(k) }
    }
  }
  return cols
}

function TableCellValue ({ value }) {
  const classes = useStyles()
  if (value === undefined) return <span className={classes.cellMuted}>&lt;missing&gt;</span>
  if (value === null) return <span className={classes.cellMuted}>null</span>
  if (typeof value === 'object') {
    return (
      <Tooltip title={<pre className={classes.cellTooltipPre}>{JSON.stringify(value, null, 2)}</pre>} arrow>
        <span className={classes.cellEllipsis}>…</span>
      </Tooltip>
    )
  }
  return <span>{String(value)}</span>
}

TableCellValue.propTypes = {
  value: PropTypes.any
}

function FieldsBody ({ analysis }) {
  const classes = useStyles()
  const fields = analysis?.fields
  if (!fields || fields.length === 0) {
    return <div className={classes.empty}>No analysis yet. Click Run to derive field accessors.</div>
  }
  return (
    <div className={classes.fieldsWrap}>
      {fields.map(field => (
        <div key={field.accessor} className={classes.fieldRow}>
          <span className={classes.fieldAccessor}>{field.accessor}</span>
          <span className={classes.fieldType}>({field.type})</span>
        </div>
      ))}
    </div>
  )
}

FieldsBody.propTypes = {
  analysis: PropTypes.object
}

// Read-only AceEditor JSON view. We pick Ace over a bare <pre> here because it
// gives us native Cmd/Ctrl+F search inside the response — the #1 ergonomic ask
// in SMT-1709 ("sometimes we just need a quick cntl+f search in the pipeline
// response to help validate a change"). Worker disabled to match JsonPanel.
function JsonAceBody ({ json }) {
  return (
    <AceEditor
      mode='json'
      theme='xcode'
      name='ddp-results-json'
      width='100%'
      height='100%'
      value={json}
      readOnly
      tabSize={2}
      showPrintMargin={false}
      editorProps={{ $blockScroll: true }}
      setOptions={{
        useWorker: false,
        showPrintMargin: false,
        showLineNumbers: true,
        wrap: false,
        highlightActiveLine: false
      }}
    />
  )
}

JsonAceBody.propTypes = {
  json: PropTypes.string.isRequired
}

// --- Tree explorer ---------------------------------------------------------
//
// Recursive collapsible JSON tree. Kept inline (no new dep) and intentionally
// simple: the goal is to give operators a navigable structure with copy-path /
// copy-value affordances and a substring filter that auto-expands ancestors of
// matches. If we later need a JSONPath query input, `json-p3` is already a dep.

function isPlainObject (v) {
  return v != null && typeof v === 'object' && !Array.isArray(v)
}

function describeContainer (value) {
  if (Array.isArray(value)) {
    return value.length === 1 ? '[ 1 item ]' : `[ ${value.length} items ]`
  }
  const keys = Object.keys(value || {})
  return keys.length === 1 ? '{ 1 key }' : `{ ${keys.length} keys }`
}

function buildJsonPath (parentPath, key, isArrayIndex) {
  if (parentPath === '') return isArrayIndex ? `$[${key}]` : `$.${key}`
  return isArrayIndex ? `${parentPath}[${key}]` : `${parentPath}.${key}`
}

// Pre-walks the tree once and returns the set of paths that should be expanded
// because they (or a descendant) match the search query. Empty query => empty
// set, and the caller falls back to the manually-toggled expanded state.
function computeMatchPaths (root, query) {
  const q = query.trim().toLowerCase()
  if (!q) return { matches: new Set(), expand: new Set() }
  const matches = new Set()
  const expand = new Set()

  const visit = (value, path, key) => {
    let hit = false
    if (key != null && String(key).toLowerCase().includes(q)) hit = true
    if (!hit && (value == null || typeof value !== 'object')) {
      if (String(value).toLowerCase().includes(q)) hit = true
    }
    let childHit = false
    if (Array.isArray(value)) {
      value.forEach((item, idx) => {
        const childPath = buildJsonPath(path, idx, true)
        if (visit(item, childPath, idx)) childHit = true
      })
    } else if (isPlainObject(value)) {
      for (const k of Object.keys(value)) {
        const childPath = buildJsonPath(path, k, false)
        if (visit(value[k], childPath, k)) childHit = true
      }
    }
    if (hit || childHit) {
      matches.add(path)
      if (childHit) expand.add(path)
      return true
    }
    return false
  }

  visit(root, '$', null)
  return { matches, expand }
}

function highlightText (text, query) {
  if (!query) return text
  const q = query.toLowerCase()
  const lower = text.toLowerCase()
  const out = []
  let i = 0
  while (i < text.length) {
    const idx = lower.indexOf(q, i)
    if (idx === -1) {
      out.push(text.slice(i))
      break
    }
    if (idx > i) out.push(text.slice(i, idx))
    out.push({ match: text.slice(idx, idx + q.length) })
    i = idx + q.length
  }
  return out
}

function HighlightedSpan ({ text, query, className }) {
  const classes = useStyles()
  if (text == null) return null
  const str = String(text)
  if (!query) return <span className={className}>{str}</span>
  const parts = highlightText(str, query)
  if (typeof parts === 'string') return <span className={className}>{parts}</span>
  return (
    <span className={className}>
      {parts.map((p, i) => typeof p === 'string'
        ? <React.Fragment key={i}>{p}</React.Fragment>
        : <span key={i} className={classes.searchHighlight}>{p.match}</span>
      )}
    </span>
  )
}

HighlightedSpan.propTypes = {
  text: PropTypes.any,
  query: PropTypes.string,
  className: PropTypes.string
}

function TreeNode ({
  nodeKey,
  isArrayIndex,
  value,
  path,
  depth,
  expandedSet,
  toggleExpanded,
  matches,
  autoExpand,
  query,
  onCopy
}) {
  const classes = useStyles()
  const isContainer = value != null && typeof value === 'object'
  const expanded = expandedSet.has(path) || autoExpand.has(path)
  const isMatch = matches.has(path)

  const keyLabel = nodeKey == null
    ? null
    : isArrayIndex
      ? <HighlightedSpan className={classes.treeIndexKey} text={`[${nodeKey}]`} query={query} />
      : <HighlightedSpan className={classes.treeKey} text={String(nodeKey)} query={query} />

  let valueNode
  if (isContainer) {
    valueNode = <span className={classes.treeValueSummary}>{describeContainer(value)}</span>
  } else if (typeof value === 'string') {
    valueNode = <HighlightedSpan className={classes.treeValueString} text={JSON.stringify(value)} query={query} />
  } else if (typeof value === 'number') {
    valueNode = <HighlightedSpan className={classes.treeValueNumber} text={String(value)} query={query} />
  } else if (typeof value === 'boolean') {
    valueNode = <HighlightedSpan className={classes.treeValueBool} text={String(value)} query={query} />
  } else if (value === null) {
    valueNode = <span className={classes.treeValueNull}>null</span>
  } else if (value === undefined) {
    valueNode = <span className={classes.treeValueNull}>undefined</span>
  } else {
    valueNode = <span>{String(value)}</span>
  }

  return (
    <>
      <div
        className={`${classes.treeRow} ${isMatch ? classes.treeRowMatch : ''}`}
        style={{ paddingLeft: 8 + depth * 14 }}
      >
        {isContainer ? (
          <span
            className={classes.treeChevron}
            onClick={() => toggleExpanded(path)}
            role='button'
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleExpanded(path) }}
          >
            {expanded ? <ExpandMoreIcon style={{ fontSize: 14 }} /> : <ChevronRightIcon style={{ fontSize: 14 }} />}
          </span>
        ) : (
          <span className={classes.treeChevronSpacer} />
        )}
        {keyLabel}
        {keyLabel && <span className={classes.treeColon}>:</span>}
        {valueNode}
        <span className={classes.treeRowActions}>
          <Tooltip title='Copy path' arrow>
            <IconButton size='small' className={classes.treeActionBtn} onClick={() => onCopy('path', path)}>
              <FileCopyIcon style={{ fontSize: 12 }} />
            </IconButton>
          </Tooltip>
          {!isContainer && (
            <Tooltip title='Copy value' arrow>
              <IconButton size='small' className={classes.treeActionBtn} onClick={() => onCopy('value', value)}>
                <FileCopyIcon style={{ fontSize: 12 }} />
              </IconButton>
            </Tooltip>
          )}
        </span>
      </div>
      {isContainer && expanded && (
        Array.isArray(value)
          ? value.map((item, idx) => (
            <TreeNode
              key={idx}
              nodeKey={idx}
              isArrayIndex
              value={item}
              path={buildJsonPath(path, idx, true)}
              depth={depth + 1}
              expandedSet={expandedSet}
              toggleExpanded={toggleExpanded}
              matches={matches}
              autoExpand={autoExpand}
              query={query}
              onCopy={onCopy}
            />
          ))
          : Object.keys(value).map((k) => (
            <TreeNode
              key={k}
              nodeKey={k}
              isArrayIndex={false}
              value={value[k]}
              path={buildJsonPath(path, k, false)}
              depth={depth + 1}
              expandedSet={expandedSet}
              toggleExpanded={toggleExpanded}
              matches={matches}
              autoExpand={autoExpand}
              query={query}
              onCopy={onCopy}
            />
          ))
      )}
    </>
  )
}

TreeNode.propTypes = {
  nodeKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isArrayIndex: PropTypes.bool.isRequired,
  value: PropTypes.any,
  path: PropTypes.string.isRequired,
  depth: PropTypes.number.isRequired,
  expandedSet: PropTypes.instanceOf(Set).isRequired,
  toggleExpanded: PropTypes.func.isRequired,
  matches: PropTypes.instanceOf(Set).isRequired,
  autoExpand: PropTypes.instanceOf(Set).isRequired,
  query: PropTypes.string,
  onCopy: PropTypes.func.isRequired
}

function TreeBody ({ result, onToast }) {
  const classes = useStyles()
  const [query, setQuery] = useState('')
  // Default-expand the root so users see top-level keys immediately. Anything
  // deeper requires either a manual click or a search-driven auto-expand.
  const [expanded, setExpanded] = useState(() => new Set(['$']))

  const { matches, expand } = useMemo(() => computeMatchPaths(result, query), [result, query])

  const toggleExpanded = useCallback((path) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(path)) next.delete(path)
      else next.add(path)
      return next
    })
  }, [])

  const onCopy = useCallback(async (kind, value) => {
    try {
      const text = kind === 'path' ? value : (typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value))
      await navigator.clipboard.writeText(text)
      onToast?.({ severity: 'success', message: kind === 'path' ? `Copied path: ${text}` : 'Copied value' })
    } catch {
      onToast?.({ severity: 'error', message: 'Copy failed' })
    }
  }, [onToast])

  if (result == null) {
    return <div className={classes.empty}>No results yet. Click Run to execute the pipeline.</div>
  }

  return (
    <div className={classes.bodyFlush}>
      <div className={classes.treeSearchRow}>
        <input
          className={classes.treeSearchInput}
          placeholder='Filter by key, path, or value…'
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <span className={classes.treeMatchCount}>
          {query ? `${matches.size} match${matches.size === 1 ? '' : 'es'}` : ''}
        </span>
      </div>
      <div className={classes.treeBody}>
        <TreeNode
          nodeKey={null}
          isArrayIndex={false}
          value={result}
          path='$'
          depth={0}
          expandedSet={expanded}
          toggleExpanded={toggleExpanded}
          matches={matches}
          autoExpand={expand}
          query={query.trim()}
          onCopy={onCopy}
        />
      </div>
    </div>
  )
}

TreeBody.propTypes = {
  result: PropTypes.any,
  onToast: PropTypes.func
}

function ResultsBody ({ result, error, mode, analysis, onToast }) {
  const classes = useStyles()
  if (mode === 'fields') return <FieldsBody analysis={analysis} />
  if (error) return <div className={classes.errorBox}>{error}</div>
  if (result == null) {
    return <div className={classes.empty}>No results yet. Click Run to execute the pipeline.</div>
  }
  if (mode === 'tree') return <TreeBody result={result} onToast={onToast} />
  if (mode === 'table' && isTableLike(result)) {
    const rows = extractRows(result)
    const flatRows = buildFlattenedRows(rows)
    const cols = buildColumnsFromFlattened(flatRows)
    return (
      <table className={classes.table}>
        <thead>
          <tr>{cols.map(c => <th key={c} className={classes.th}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {flatRows.map((row, idx) => (
            <tr key={idx}>
              {cols.map(c => (
                <td key={c} className={classes.td}>
                  <TableCellValue value={row[c]} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    )
  }
  return <JsonAceBody json={JSON.stringify(result, null, 2)} />
}

ResultsBody.propTypes = {
  result: PropTypes.any,
  error: PropTypes.string,
  mode: PropTypes.oneOf(['json', 'table', 'fields', 'tree']).isRequired,
  analysis: PropTypes.object,
  onToast: PropTypes.func
}

// Wrap the JSON Ace editor so it fills its parent flex column. The Ace
// component needs an explicit height to size itself, hence the flex shim.
function JsonModeWrap ({ children }) {
  return <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>{children}</div>
}

JsonModeWrap.propTypes = {
  children: PropTypes.node
}

function ResultsToolbar ({
  mode,
  setMode,
  tableSupported,
  fieldsSupported,
  treeSupported,
  onPopout,
  popoutDisabled,
  onCopy,
  copyDisabled,
  copyState
}) {
  const classes = useStyles()
  const copyLabel = copyState === 'copied' ? 'Copied!' : copyState === 'error' ? 'Copy failed' : 'Copy JSON'
  return (
    <div className={classes.toolbar}>
      <div className={classes.modeToggle}>
        <button
          type='button'
          className={`${classes.modeBtn} ${mode === 'json' ? classes.modeBtnActive : ''}`}
          onClick={() => setMode('json')}
        >
          json
        </button>
        <button
          type='button'
          className={`${classes.modeBtn} ${mode === 'tree' ? classes.modeBtnActive : ''}`}
          onClick={() => setMode('tree')}
          disabled={!treeSupported}
        >
          tree
        </button>
        <button
          type='button'
          className={`${classes.modeBtn} ${mode === 'table' ? classes.modeBtnActive : ''}`}
          onClick={() => setMode('table')}
          disabled={!tableSupported}
        >
          table
        </button>
        <button
          type='button'
          className={`${classes.modeBtn} ${mode === 'fields' ? classes.modeBtnActive : ''}`}
          onClick={() => setMode('fields')}
          disabled={!fieldsSupported}
        >
          fields
        </button>
      </div>
      <div className={classes.spacer} />
      {onCopy && (
        <Tooltip title={copyLabel} arrow>
          <span>
            <IconButton size='small' className={classes.iconBtn} onClick={onCopy} disabled={copyDisabled}>
              <FileCopyIcon style={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {onPopout && (
        <Tooltip title='Open in modal' arrow>
          <span>
            <IconButton size='small' className={classes.iconBtn} onClick={onPopout} disabled={popoutDisabled}>
              <FullscreenIcon style={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </div>
  )
}

ResultsToolbar.propTypes = {
  mode: PropTypes.oneOf(['json', 'table', 'fields', 'tree']).isRequired,
  setMode: PropTypes.func.isRequired,
  tableSupported: PropTypes.bool.isRequired,
  fieldsSupported: PropTypes.bool.isRequired,
  treeSupported: PropTypes.bool.isRequired,
  onPopout: PropTypes.func,
  popoutDisabled: PropTypes.bool,
  onCopy: PropTypes.func,
  copyDisabled: PropTypes.bool,
  copyState: PropTypes.oneOf(['idle', 'copied', 'error'])
}

function ResultsView ({ result, error }) {
  const classes = useStyles()
  const { analysis } = useEditor()
  const [mode, setMode] = useState('json')
  const [popped, setPopped] = useState(false)
  const [copyState, setCopyState] = useState('idle')
  const [toast, setToast] = useState(null)

  const tableSupported = useMemo(() => isTableLike(result), [result])
  const fieldsSupported = !!analysis?.fields?.length
  const treeSupported = result != null

  const handleCopy = useCallback(async () => {
    if (result == null) return
    try {
      await navigator.clipboard.writeText(JSON.stringify(result, null, 2))
      setCopyState('copied')
      setTimeout(() => setCopyState('idle'), 1500)
    } catch {
      setCopyState('error')
      setTimeout(() => setCopyState('idle'), 1500)
    }
  }, [result])

  // The JSON view uses Ace, which needs a flexbox container with explicit
  // height. Keep the table/fields/tree modes inside the scrolling div as
  // before so their natural overflow is preserved.
  const isAceMode = mode === 'json' && !error && result != null
  const bodyClass = (mode === 'tree' && treeSupported && !error) ? classes.bodyFlush : classes.body

  return (
    <div className={classes.wrap}>
      <ResultsToolbar
        mode={mode}
        setMode={setMode}
        tableSupported={tableSupported}
        fieldsSupported={fieldsSupported}
        treeSupported={treeSupported}
        onPopout={() => setPopped(true)}
        popoutDisabled={result == null && !error && !fieldsSupported}
        onCopy={handleCopy}
        copyDisabled={result == null}
        copyState={copyState}
      />
      <div className={isAceMode ? classes.bodyFlush : bodyClass}>
        {isAceMode
          ? <JsonModeWrap><JsonAceBody json={JSON.stringify(result, null, 2)} /></JsonModeWrap>
          : <ResultsBody result={result} error={error} mode={mode} analysis={analysis} onToast={setToast} />}
      </div>

      <Dialog open={popped} onClose={() => setPopped(false)} classes={{ paper: classes.modalPaper }} maxWidth={false}>
        <div className={classes.modalHeader}>
          <span className={classes.modalTitle}>Pipeline Result</span>
          <IconButton size='small' onClick={() => setPopped(false)}>
            <CloseIcon fontSize='small' />
          </IconButton>
        </div>
        <DialogContent className={classes.modalContent}>
          <ResultsToolbar
            mode={mode}
            setMode={setMode}
            tableSupported={tableSupported}
            fieldsSupported={fieldsSupported}
            treeSupported={treeSupported}
            onCopy={handleCopy}
            copyDisabled={result == null}
            copyState={copyState}
          />
          <div className={isAceMode ? classes.bodyFlush : bodyClass}>
            {isAceMode
              ? <JsonModeWrap><JsonAceBody json={JSON.stringify(result, null, 2)} /></JsonModeWrap>
              : <ResultsBody result={result} error={error} mode={mode} analysis={analysis} onToast={setToast} />}
          </div>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={2500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast && (
          <Alert onClose={() => setToast(null)} severity={toast.severity} variant='filled' elevation={6}>
            {toast.message}
          </Alert>
        )}
      </Snackbar>
    </div>
  )
}

ResultsView.propTypes = {
  result: PropTypes.any,
  error: PropTypes.string
}

export default ResultsView
